import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronUp,
  Inbox,
  Loader2,
  MapPin,
  User,
} from 'lucide-react'
import { ServiceCategoryIcon } from '@/components/icons/ServiceCategoryIcons'
import { JobStatusBadge } from '@/components/shared/StatusBadges'
import { Skeleton } from '@/components/ui/skeleton'
import { useUnassignedJobs } from '@/hooks/useJobs'
import { formatTimeslot } from '@/components/jobs/agenda/grid-helpers'
import {
  clearAgendaDragPayload,
  getAgendaDragPayload,
  setAgendaDragPreview,
  setAgendaDragPayload,
  updateAgendaDragPreview,
} from '@/components/jobs/agenda/agenda-dnd'
import type { UnassignJobOptions } from '@/components/jobs/AgendaGrid'
import { getJobDurationMinutes } from '@/components/jobs/agenda/time-geometry'
import {
  PRELOAD_ROOT_PROPS,
  jobPreloadTargetProps,
  parsePreloadJobIds,
} from '@/components/jobs/agenda/job-detail-preload'
import { useJobDetailPreloadQueue } from '@/components/jobs/agenda/useAgendaJobPreload'
import { JOB_STATUS, type Job } from '@/types'
import { formatDate } from '@/lib/format'
import { todayIsoDate } from '@/lib/date'

// Tray cards are 232px wide with a 12px gap; used to size the first screenful
// preloaded while the tray is collapsed (its cards have no layout height then).
const TRAY_CARD_PITCH_PX = 244
const TRAY_PRELOAD_MAX_FIRST_SCREEN = 12

interface UnassignedTrayProps {
  onJobClick: (job: Job) => void
  onUnassignJob?: (jobId: string, options?: UnassignJobOptions) => void
  isMutatingJobId?: string | null
  enableDragDropAssignment?: boolean
}

function formatJobLocation(job: Job): string | null {
  const parts = [job.postal_code, job.address, job.city].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

function isLateToPlan(job: Job, todayIso: string): boolean {
  if (job.status === JOB_STATUS.to_plan) {
    return Boolean(job.callback_date && job.callback_date < todayIso)
  }
  return Boolean(
    job.status === JOB_STATUS.planned &&
    job.scheduled_date &&
    job.scheduled_date < todayIso
  )
}

function callbackTimeKey(time: string | null): string {
  if (time === '08:00') return 'agenda.callbackTimes.morning'
  if (time === '14:00') return 'agenda.callbackTimes.afternoon'
  return 'agenda.callbackTimes.other'
}

function getPlanningTypeKey(job: Job): string {
  if (job.contract_id) return 'agenda.card.underContract'
  return 'agenda.card.siteVisitQuote'
}

// 2.3.2 Unassigned jobs section — count badge, search, sorted ascending.
// Clicking an entry opens the job-details drawer over the agenda (handled by
// the parent AgendaPage).
export function UnassignedTray({
  onJobClick,
  onUnassignJob,
  isMutatingJobId,
  enableDragDropAssignment = true,
}: UnassignedTrayProps) {
  const { t } = useTranslation('jobs')
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useUnassignedJobs({
    sort_column: 'scheduled_date',
    sort_order: 'asc',
  })

  const jobs = data?.pages.flatMap(page => page.data) ?? []
  const count = data?.pages[0]?.metadata?.total ?? jobs.length
  const todayIso = todayIsoDate()
  const lateCount = jobs.filter(job => isLateToPlan(job, todayIso)).length
  const [isExpanded, setIsExpanded] = useState(false)
  const [isDropActive, setIsDropActive] = useState(false)
  const isDropBlocked = isDropActive && Boolean(isMutatingJobId)
  const canDragDropAssignments =
    enableDragDropAssignment && Boolean(onUnassignJob)

  const toggleExpanded = () => setIsExpanded(open => !open)

  // Preload the first screenful of tray jobs: behind the calendar while the
  // tray is collapsed (ready the moment it opens), ahead of the calendar once
  // it is expanded, since a user who opens the tray tends to open its cards.
  const preloadQueue = useJobDetailPreloadQueue()
  const trayRef = useRef<HTMLElement>(null)
  const leadingJobIdsKey = jobs
    .slice(0, TRAY_PRELOAD_MAX_FIRST_SCREEN)
    .map(job => job.id)
    .join(',')
  useEffect(() => {
    if (!preloadQueue) return
    const trayWidth = trayRef.current?.clientWidth ?? 0
    const firstScreenCount = trayWidth
      ? Math.ceil(trayWidth / TRAY_CARD_PITCH_PX)
      : TRAY_PRELOAD_MAX_FIRST_SCREEN
    const firstScreen = parsePreloadJobIds(leadingJobIdsKey).slice(
      0,
      firstScreenCount
    )
    // Set the new owner before clearing the old one so the jobs stay listed
    // across the toggle and are not treated as newly arrived (and refetched).
    preloadQueue.setSource(
      isExpanded ? 'trayOpen' : 'trayCollapsed',
      firstScreen
    )
    preloadQueue.setSource(isExpanded ? 'trayCollapsed' : 'trayOpen', [])
  }, [preloadQueue, isExpanded, leadingJobIdsKey])
  useEffect(() => {
    if (!preloadQueue) return
    return () => {
      preloadQueue.setSource('trayOpen', [])
      preloadQueue.setSource('trayCollapsed', [])
    }
  }, [preloadQueue])

  const getUnassignPayload = (dataTransfer: DataTransfer) => {
    if (!canDragDropAssignments) return null
    const payload = getAgendaDragPayload(dataTransfer)
    return payload?.type === 'scheduled-job' &&
      payload.status === JOB_STATUS.assigned
      ? payload
      : null
  }

  // The list pages server-side (count can be in the hundreds). Auto-load the
  // next page as the user scrolls the tray to its end so every unassigned job
  // is reachable — not just the first page (bug 2.3.2: count showed all but the
  // list only rendered page 1).
  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const getDropInsertBeforeJobId = (clientX: number): string | undefined => {
    const tray = scrollRef.current
    if (!tray) return undefined

    const cards = Array.from(
      tray.querySelectorAll<HTMLElement>('[data-unassigned-job-id]')
    )
    const target = cards.find(card => {
      const rect = card.getBoundingClientRect()
      return clientX < rect.left + rect.width / 2
    })

    return target?.dataset.unassignedJobId
  }

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasNextPage) return
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          void fetchNextPage()
        }
      },
      { root: scrollRef.current, rootMargin: '0px 200px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, jobs.length])

  return (
    <section
      ref={trayRef}
      onDragEnter={
        canDragDropAssignments
          ? event => {
              if (getUnassignPayload(event.dataTransfer)) setIsDropActive(true)
            }
          : undefined
      }
      onDragOver={
        canDragDropAssignments
          ? event => {
              if (!getUnassignPayload(event.dataTransfer)) return
              event.preventDefault()
              event.dataTransfer.dropEffect = isDropBlocked ? 'none' : 'move'
              updateAgendaDragPreview(event.clientX, event.clientY, null)
            }
          : undefined
      }
      onDragLeave={
        canDragDropAssignments
          ? event => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                setIsDropActive(false)
              }
            }
          : undefined
      }
      onDrop={
        canDragDropAssignments
          ? event => {
              const payload = getUnassignPayload(event.dataTransfer)
              if (!payload) return
              event.preventDefault()
              setIsDropActive(false)
              if (!isDropBlocked) {
                onUnassignJob?.(payload.jobId, {
                  insertBeforeJobId: getDropInsertBeforeJobId(event.clientX),
                })
              }
              clearAgendaDragPayload()
            }
          : undefined
      }
      className={`overflow-hidden rounded-2xl border border-dashed border-[#b0b5c3] bg-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)] transition-colors ${
        isDropActive
          ? isDropBlocked
            ? 'outline outline-2 outline-offset-[-2px] outline-[#b0b5c3]'
            : 'bg-[#fff4ee] outline outline-2 outline-offset-[-2px] outline-[#ff5a1f]'
          : ''
      }`}
    >
      <button
        type='button'
        onClick={toggleExpanded}
        className='flex h-14 w-full items-center justify-between gap-3 px-4 text-left'
        aria-expanded={isExpanded}
      >
        <div className='flex min-w-0 items-center gap-3'>
          <span className='flex shrink-0 items-center gap-2'>
            <Inbox className='h-4 w-4 shrink-0 text-[#272c36]' />
            <span className='shrink-0 font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
              {t('agenda.toPlanTitle')}
            </span>
          </span>
          <h2 className='sr-only'>{t('agenda.unassignedTitle')}</h2>
          <span className='flex h-5 min-w-5 shrink-0 items-center justify-center rounded-lg bg-[#fecaca] px-1.5 font-sans text-[10px] font-medium leading-4 text-[#7f1d1d]'>
            {count}
          </span>
          <span className='min-w-0 truncate font-sans text-sm font-normal leading-[1.4] text-[#3d434e]'>
            <strong className='font-sans text-sm font-medium leading-[1.4] text-[#d63b33]'>
              {t('agenda.lateCount', { count: lateCount })}
            </strong>{' '}
            <span className='text-[#8a91a0]'>•</span> {t('agenda.toPlanHint')}
          </span>
        </div>
        <span className='inline-flex h-8 shrink-0 items-center gap-1.5 rounded-xl bg-[#fff1ea] px-3 font-sans text-xs font-medium leading-none text-[#ff5a1f]'>
          {isExpanded ? t('agenda.foldBack') : t('agenda.open')}
          {isExpanded ? (
            <ChevronUp className='h-4 w-4' />
          ) : (
            <ChevronDown className='h-4 w-4' />
          )}
        </span>
      </button>

      <div
        className={`transition-[height,padding] duration-200 ${
          isExpanded ? 'h-[148px] px-4 pb-4' : 'h-0 p-0'
        }`}
      >
        {isLoading && <Skeleton className='h-24 w-full' />}

        {isError && !isLoading && (
          <p className='text-sm text-destructive'>
            {t('agenda.unassignedLoadError')}
          </p>
        )}

        {!isLoading && !isError && jobs.length === 0 && (
          <p className='text-sm text-muted-foreground'>
            {t('agenda.noUnassigned')}
          </p>
        )}

        {!isLoading && !isError && jobs.length > 0 && (
          <div
            ref={scrollRef}
            className='flex h-full items-start gap-3 overflow-x-auto overflow-y-hidden pb-2 [scrollbar-color:#cdcdcd_transparent]'
            {...PRELOAD_ROOT_PROPS}
          >
            {jobs.map(job => {
              const isToPlan = job.status === JOB_STATUS.to_plan
              const timeslot = formatTimeslot(
                job,
                t('agenda.monthHover.noTime')
              ).replace(' - ', '-')
              const location = formatJobLocation(job)
              const postalCode = job.postal_code ?? job.city ?? '--'
              const city = job.city ?? job.address ?? location ?? '--'
              const serviceTitle = isToPlan
                ? t('agenda.notPlannedYet')
                : (job.service_type ??
                  job.reference ??
                  job.client?.name ??
                  job.client_name ??
                  t('common.clientFallback'))
              const callbackDescription = isToPlan
                ? job.callback_time === '00:00' || job.callback_time === null
                  ? t('agenda.callbackDescriptionDateOnly', {
                      date: formatDate(job.callback_date),
                    })
                  : t('agenda.callbackDescription', {
                      time: t(callbackTimeKey(job.callback_time)),
                      date: formatDate(job.callback_date),
                    })
                : null
              const planningType = t(getPlanningTypeKey(job))
              const isMutating = job.id === isMutatingJobId
              return (
                <button
                  key={job.id}
                  type='button'
                  onClick={() => onJobClick(job)}
                  draggable={canDragDropAssignments && !isMutating}
                  onDragStart={
                    canDragDropAssignments
                      ? event => {
                          setAgendaDragPayload(event.dataTransfer, {
                            type: 'unassigned-job',
                            jobId: job.id,
                            durationMinutes: getJobDurationMinutes(job),
                            grabOffsetY:
                              event.clientY -
                              event.currentTarget.getBoundingClientRect().top,
                            startTime: job.start_time,
                            endTime: job.end_time,
                          })
                          setAgendaDragPreview(
                            event.dataTransfer,
                            event.currentTarget,
                            event.clientX,
                            event.clientY
                          )
                          event.dataTransfer.effectAllowed = 'move'
                        }
                      : undefined
                  }
                  onDragEnd={
                    canDragDropAssignments ? clearAgendaDragPayload : undefined
                  }
                  aria-busy={isMutating || undefined}
                  disabled={isMutating}
                  data-unassigned-job-id={job.id}
                  {...(isExpanded ? jobPreloadTargetProps(job, 'tray') : null)}
                  className='flex h-28 w-[232px] shrink-0 overflow-hidden rounded-xl border border-[#e7e9ef] bg-white p-0 text-left shadow-[0_1px_4px_0_rgba(38,45,66,0.05),0_1px_4px_0_rgba(38,45,66,0.1)] transition hover:-translate-y-px hover:shadow-[0_8px_16px_-8px_rgba(38,45,66,0.22)] disabled:cursor-wait disabled:opacity-60'
                >
                  <span className='flex w-[90px] shrink-0 flex-col gap-1 bg-[#f4f5f8] p-2 text-[#171a20]'>
                    <MapPin className='h-3 w-3 shrink-0' aria-hidden='true' />
                    <span className='min-w-0 truncate font-heading text-xl font-bold leading-[1.2]'>
                      {postalCode}
                    </span>
                    <span className='line-clamp-2 min-w-0 whitespace-normal break-words font-sans text-xs font-medium leading-4'>
                      {city}
                    </span>
                    <span className='mt-auto inline-flex w-fit max-w-full rounded-md border border-[#e7e9ef] bg-white px-1 py-0.5 font-sans text-[10px] font-medium leading-3 tabular-nums text-[#171a20]'>
                      <span className='truncate'>{timeslot}</span>
                    </span>
                  </span>
                  <span className='flex min-w-0 flex-1 flex-col gap-2 p-2'>
                    <span className='flex min-w-0 items-start gap-1'>
                      <ServiceCategoryIcon
                        name={serviceTitle}
                        className='mt-0.5 h-4 w-4 shrink-0'
                      />
                      <span className='line-clamp-2 min-w-0 font-sans text-xs font-medium leading-4 text-[#171a20]'>
                        {serviceTitle}
                      </span>
                      {isToPlan && (
                        <JobStatusBadge
                          status={job.status}
                          className='ml-auto shrink-0 rounded-md px-1.5 py-0.5 font-sans text-[8px] uppercase leading-[10px]'
                        />
                      )}
                    </span>
                    {callbackDescription ? (
                      <span className='line-clamp-3 max-h-[42px] whitespace-normal break-words text-[10px] font-normal leading-[14px] text-[#3d434e]'>
                        {callbackDescription}
                      </span>
                    ) : (
                      <span className='flex min-h-[32px] flex-col gap-1'>
                        <span className='inline-flex w-fit max-w-full rounded-md border border-[#e7e9ef] bg-[#f4f5f8] px-1.5 py-0.5 font-sans text-[8px] font-medium uppercase leading-[10px] text-[#171a20] shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'>
                          <span className='truncate'>{planningType}</span>
                        </span>
                        <JobStatusBadge
                          status={job.status}
                          className='ml-auto w-fit max-w-full rounded-md px-1.5 py-0.5 font-sans text-[8px] uppercase leading-[10px] shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'
                        />
                      </span>
                    )}
                    <span className='mt-auto flex min-w-0 items-center gap-1 border-t border-[#e7e9ef] pt-1 font-sans text-[10px] font-medium leading-3 text-[#3d434e]'>
                      <User
                        className='h-3 w-3 shrink-0 text-[#555b6a]'
                        aria-hidden='true'
                      />
                      <span className='min-w-0 truncate'>--</span>
                    </span>
                  </span>
                </button>
              )
            })}
            {hasNextPage && (
              <div
                ref={sentinelRef}
                className='flex min-w-[60px] shrink-0 items-center justify-center'
              >
                {isFetchingNextPage && (
                  <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
